/**
 * utils/alarmSound.ts
 * Web Audio API tone generation for the clock alarm system.
 *
 * Provides 5 built-in alarm tones synthesized at runtime via the Web Audio API.
 * No bundled audio files — all tones are generated programmatically using
 * OscillatorNode + GainNode scheduling.
 *
 * AudioContext is created lazily on first playback request. Browser autoplay
 * policy is handled by resuming suspended contexts (the Live Trading phase
 * requires user interaction to enter, satisfying the gesture requirement).
 *
 * Includes `webkitAudioContext` fallback for older Safari versions.
 *
 * @see types/shared/AlarmRule.ts — AlarmToneId type definition
 * @see constants/alarmTone.ts — display metadata for settings UI
 */

import type { AlarmToneId } from '@/types/shared/AlarmRule';

// ---------------------------------------------------------------------------
// AudioContext singleton (lazy — no side effect on import)
// ---------------------------------------------------------------------------

/** Module-level AudioContext instance, created on first use. */
let audioContext: AudioContext | null = null;

/**
 * Resolve the AudioContext constructor for the current environment.
 * Falls back to `webkitAudioContext` for older Safari.
 * Returns `undefined` when Web Audio API is completely unavailable.
 */
function getAudioContextConstructor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  // SSR/test guard: window may not exist in non-browser runtimes
  if (typeof window === 'undefined') return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  if (typeof win.webkitAudioContext !== 'undefined') return win.webkitAudioContext;
  return undefined;
}

/**
 * Get or create the shared AudioContext instance.
 * Returns `null` when the Web Audio API is unavailable in the current environment.
 */
function getOrCreateAudioContext(): AudioContext | null {
  if (audioContext) return audioContext;

  const Ctor = getAudioContextConstructor();
  if (!Ctor) return null;

  try {
    audioContext = new Ctor();
    return audioContext;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Tone generators (private)
// ---------------------------------------------------------------------------

/**
 * Schedule a single oscillator note with gain envelope.
 * Utility shared by individual tone generators.
 */
function scheduleNote(
  ctx: AudioContext,
  startTime: number,
  duration: number,
  frequency: number,
  waveform: 'sine' | 'triangle' | 'square' | 'sawtooth',
  peakGain: number,
  attackTime: number,
  decayTime: number,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = waveform;
  osc.frequency.setValueAtTime(frequency, startTime);

  // Envelope: silence → attack → peak → decay → silence
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(peakGain, startTime + attackTime);
  gain.gain.setValueAtTime(peakGain, startTime + duration - decayTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(startTime);
  osc.stop(startTime + duration);
}

/**
 * Chime — Pleasant, melodic. Two ascending sine notes (C5 → E5).
 */
function playChime(ctx: AudioContext, time: number): void {
  // Note 1: C5 (523 Hz)
  scheduleNote(ctx, time, 0.18, 523.25, 'sine', 0.35, 0.02, 0.06);
  // Note 2: E5 (659 Hz) — starts after a short gap
  scheduleNote(ctx, time + 0.2, 0.22, 659.25, 'sine', 0.3, 0.02, 0.08);
}

/**
 * Bell — Clear, resonant. Triangle wave at A4 with long exponential decay.
 */
function playBell(ctx: AudioContext, time: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'triangle';
  osc.frequency.setValueAtTime(440, time);
  // Slight detuning for warmth
  osc.detune.setValueAtTime(5, time);

  // Sharp attack, long exponential decay
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(0.4, time + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.8);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(time);
  osc.stop(time + 0.8);
}

/**
 * Alert — Attention-getting. Two alternating square wave tones, repeated twice.
 */
function playAlert(ctx: AudioContext, time: number): void {
  // Pattern: high → low → high → low (4 notes total)
  const pattern = [800, 600, 800, 600];
  const noteDuration = 0.1;
  const gap = 0.02;

  pattern.forEach((freq, i) => {
    const noteStart = time + i * (noteDuration + gap);
    scheduleNote(ctx, noteStart, noteDuration, freq, 'square', 0.15, 0.005, 0.02);
  });
}

/**
 * Gentle Ping — Soft, brief. Single high sine tone with fast exponential decay.
 */
function playGentlePing(ctx: AudioContext, time: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(1200, time);

  // Very fast attack, quick decay
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(0.25, time + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(time);
  osc.stop(time + 0.15);
}

/**
 * Urgent — Rapid pulsing. Square wave at 900 Hz, 4 short pulses.
 */
function playUrgent(ctx: AudioContext, time: number): void {
  const pulseCount = 4;
  const onDuration = 0.06;
  const offDuration = 0.05;

  for (let i = 0; i < pulseCount; i++) {
    const pulseStart = time + i * (onDuration + offDuration);
    scheduleNote(ctx, pulseStart, onDuration, 900, 'square', 0.2, 0.003, 0.01);
  }
}

// ---------------------------------------------------------------------------
// Internal tone lookup (not imported from constants — implementation detail)
// ---------------------------------------------------------------------------

/** Maps each AlarmToneId to its generator function. */
const TONE_GENERATORS: Record<AlarmToneId, (ctx: AudioContext, time: number) => void> = {
  'chime': playChime,
  'bell': playBell,
  'alert': playAlert,
  'gentle-ping': playGentlePing,
  'urgent': playUrgent,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Play a built-in alarm tone by ID.
 *
 * Creates the AudioContext lazily on first call. If the context is suspended
 * (browser autoplay policy), it attempts to resume before playing. Silently
 * returns (no-throw) if audio is unavailable or the context cannot resume.
 *
 * @param toneId - Which built-in tone to play
 * @returns Resolves when the tone has been scheduled (not when playback finishes)
 *
 * @example
 * ```typescript
 * // Fire-and-forget — safe to call without try/catch
 * await playAlarmTone('chime');
 * ```
 */
export async function playAlarmTone(toneId: AlarmToneId): Promise<void> {
  const ctx = getOrCreateAudioContext();
  if (!ctx) return;

  // Resume suspended context (autoplay policy)
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return; // Cannot resume — silently bail
    }
  }

  // Still not running after resume attempt
  if (ctx.state !== 'running') return;

  const generator = TONE_GENERATORS[toneId];
  generator(ctx, ctx.currentTime);
}

/**
 * Attempt to unlock the AudioContext after a user interaction.
 *
 * Call this from a click or touch handler to satisfy browser autoplay policy.
 * The Live Trading phase transition inherently provides this gesture, but
 * this function is exposed for explicit gating if needed.
 *
 * @returns `true` if audio is now available, `false` if unavailable or resume failed
 *
 * @example
 * ```typescript
 * // In a click handler
 * const unlocked = await unlockAudio();
 * if (unlocked) console.log('Audio ready');
 * ```
 */
export async function unlockAudio(): Promise<boolean> {
  const ctx = getOrCreateAudioContext();
  if (!ctx) return false;

  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }

  return ctx.state === 'running';
}

/**
 * Check if audio playback is available in the current environment.
 *
 * Returns `true` if the Web Audio API (`AudioContext` or `webkitAudioContext`)
 * exists. Does not check whether the context is suspended — use `unlockAudio()`
 * to handle that.
 *
 * @returns `true` if Web Audio API is available
 *
 * @example
 * ```typescript
 * if (!isAudioAvailable()) {
 *   console.log('Web Audio API not supported');
 * }
 * ```
 */
export function isAudioAvailable(): boolean {
  return getAudioContextConstructor() !== undefined;
}

/**
 * Clean up the AudioContext and release resources.
 *
 * Call this when the alarm engine unmounts (e.g., leaving Live Trading phase).
 * The context will be recreated lazily on the next `playAlarmTone()` call.
 *
 * @example
 * ```typescript
 * // In useEffect cleanup
 * return () => { void disposeAudio(); };
 * ```
 */
export async function disposeAudio(): Promise<void> {
  if (!audioContext) return;

  try {
    await audioContext.close();
  } catch {
    // Already closed or in an error state — safe to ignore
  }

  audioContext = null;
}
